import styled from 'styled-components';
import type { FormatNodeValue, TreemapHierarchyNode } from '../../types';
import {
  HIERARCHICAL_TREEMAP_CORNER_RADIUS,
  HIERARCHICAL_TREEMAP_FONT_SIZE,
  HIERARCHICAL_TREEMAP_LINE_HEIGHT,
  HIERARCHICAL_TREEMAP_PADDING,
  measureHierarchicalTreemapLabel,
} from './consts';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { IconSize, NEGATIVE_COLOR_CSS_VARIABLE } from '@ardoq/icons';
import { hyphenateWrap } from './hyphenateWrap';
import { toTileText } from './toTileText';
import type { HierarchyNodeA11yProps } from '../useHierarchyNavigation';

/**
 * A group is drawn the way the block diagram draws one: a neutral card, striped along the top
 * with the component's own color, its name on the stripe and its grouping rule's label on the
 * band beneath. A leaf fills with that color outright, as the component matrix does.
 */
const NodeElement = styled.div<{ $hasCard: boolean }>`
  position: absolute;
  border-radius: ${HIERARCHICAL_TREEMAP_CORNER_RADIUS}px;
  overflow: hidden;
  box-sizing: border-box;
  ${({ $hasCard }) =>
    $hasCard &&
    `background: ${colors.grey50}; border: 1px solid ${colors.grey400};`}

  /* Drawn inside the tile, because tiles abut: an outline outside the border is painted over by
     whichever neighbour renders next, and the focused tile is the one that then looks unfocused. */
  &:focus-visible {
    outline: 2px solid ${colors.borderFocus};
    outline-offset: -2px;
  }
`;
const Stripe = styled.div`
  display: flex;
  align-items: center;
  gap: ${HIERARCHICAL_TREEMAP_PADDING}px;
  height: ${HIERARCHICAL_TREEMAP_LINE_HEIGHT}px;
  padding: 0 ${HIERARCHICAL_TREEMAP_PADDING}px;
`;
const LeafContent = styled.div`
  padding: ${HIERARCHICAL_TREEMAP_PADDING}px
    ${2 * HIERARCHICAL_TREEMAP_PADDING}px;
`;
const IconContainer = styled.div<{ $isDark: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $isDark }) =>
    $isDark ? colors.iconOnInvertedStrong : colors.iconStrong};
  ${NEGATIVE_COLOR_CSS_VARIABLE}: ${({ $isDark }) =>
    $isDark ? colors.transparent0 : colors.white950};
`;
const Label = styled.label`
  margin: 0;
  font: ${HIERARCHICAL_TREEMAP_FONT_SIZE}px ${ARDOQ_DEFAULT_FONT_FAMILY};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
/* The lines `toLeafLabelLines` measured, drawn at the line height it measured them against.
   No wrapping or ellipsis here: both already happened, and letting CSS have a second go at it
   is what split narrow tiles' words a letter to a line. */
const LeafLabel = styled.div`
  min-width: 0;
  flex: 1;
`;
const LeafLabelLine = styled.div`
  font: ${HIERARCHICAL_TREEMAP_FONT_SIZE}px/${HIERARCHICAL_TREEMAP_LINE_HEIGHT}px
    ${ARDOQ_DEFAULT_FONT_FAMILY};
  white-space: pre;
  overflow: hidden;
`;
const SubLabel = styled(Label)`
  display: block;
  height: ${HIERARCHICAL_TREEMAP_LINE_HEIGHT}px;
  padding: 0 ${HIERARCHICAL_TREEMAP_PADDING}px;
  color: ${colors.textSubtle};
`;

type HierarchicalTreemapViewNodeProperties = {
  node: TreemapHierarchyNode;
  /** Whether any of its children survived the Maximum depth setting, not whether it has any. */
  hasDrawnChildren: boolean;
  formatValue: FormatNodeValue;
  /** Role, name and the roving `tabIndex`, from the one place that knows the whole tree. */
  a11yProps: HierarchyNodeA11yProps;
};

const HierarchicalTreemapViewNode = (
  props: HierarchicalTreemapViewNodeProperties
) => {
  const {
    x0,
    x1,
    y0,
    y1,
    value,
    data: { label, subLabel, modelId, color, isDark, representationData },
  } = props.node;
  const valueLabel = props.formatValue(value);
  const isGroup = props.hasDrawnChildren;
  /**
   * A group whose children the depth setting cut, and which has no colour to fill with.
   *
   * `reduceNodes` gives a rule-built group `color: null` by design - it is a grouping of
   * components rather than one of them - so the leaf branch's `color ?? colors.surfaceDefault`
   * is `white950` on a pane whose `ViewContainer` is also `white950`, and the leaf branch draws
   * no border. The tile disappears: a name and a value floating on nothing, in the view read by
   * area. It also drops `subLabel`, which is the only thing saying what the group is a group
   * *of*.
   *
   * Not reachable on a parent-grouped viewpoint, where every group is a component with its own
   * colour. Reachable on any of the ordinary rules - component type, workspace, field value,
   * reference, tag - at the depth those groups sit at.
   */
  const isCutRuleGroup =
    !isGroup && Boolean(props.node.children?.length) && color === null;
  /* Grey card and border: what gives a colourless tile a shape. A cut group that *has* a colour
     fills with it instead, which is the point of drawing one as a leaf at all. */
  const hasCard = isGroup || isCutRuleGroup;
  const hasIcon = Boolean(modelId && representationData);
  const innerHeight = y1 - y0 - 2 * HIERARCHICAL_TREEMAP_PADDING;
  /* Only where a line is left over for the name. `toLeafText` reads a non-positive height as a
     tile nothing has measured yet and answers with the whole untruncated label, so reserving a
     line off a tile two lines short of holding one costs the name its ellipsis and the value its
     place - and clips the sublabel out of the tile it was reserved in. */
  const showSubLabel =
    Boolean(subLabel) && innerHeight >= 2 * HIERARCHICAL_TREEMAP_LINE_HEIGHT;
  const { lines: leafLines, showIcon } = toTileText({
    label,
    valueLabel: `(${valueLabel})`,
    availableWidth: x1 - x0 - 4 * HIERARCHICAL_TREEMAP_PADDING,
    /* The sublabel's line comes off the top, the way `toGroupHeaderHeight` takes it for a group
       that draws one. `NodeElement` is `overflow: hidden`, so without this the label is clipped
       away silently whenever the name and value use the last line available to them. */
    availableHeight:
      innerHeight - (showSubLabel ? HIERARCHICAL_TREEMAP_LINE_HEIGHT : 0),
    iconWidth: hasIcon
      ? HIERARCHICAL_TREEMAP_FONT_SIZE + HIERARCHICAL_TREEMAP_PADDING
      : 0,
    measure: measureHierarchicalTreemapLabel,
    lineHeight: HIERARCHICAL_TREEMAP_LINE_HEIGHT,
    wrap: hyphenateWrap,
  });
  const textColor = isDark ? colors.textOnInvertedStrong : colors.textDefault;
  const icon = modelId && representationData && (
    <IconContainer $isDark={isDark}>
      <ComponentRepresentation
        iconSize={HIERARCHICAL_TREEMAP_FONT_SIZE as IconSize}
        {...representationData}
      />
    </IconContainer>
  );

  return (
    <NodeElement
      $hasCard={hasCard}
      style={{
        left: x0,
        width: x1 - x0,
        top: y0,
        height: y1 - y0,
        ...(hasCard ? {} : { background: color ?? colors.surfaceDefault }),
      }}
      className="component"
      data-component-id={modelId ?? undefined}
      {...props.a11yProps}
    >
      {/* The drawn text is hidden from the accessibility tree, as the two SVG views' label
          groups are. `aria-label` on the tile wins the *name*, but it does not remove the
          descendants: a reader browsing linearly would hear the name and then the same label and
          value again as loose text. Set on each container that holds text rather than on one
          wrapper around them, because a wrapper is a box and these two branches' boxes are load
          bearing. */}
      {isGroup ? (
        <>
          <Stripe
            aria-hidden="true"
            style={{ background: color ?? colors.grey50, color: textColor }}
          >
            {icon}
            <Label style={{ flex: '0 1 auto', color: textColor }}>
              {label}
            </Label>
            <Label
              style={{ flex: '1 0 auto', marginLeft: 3, color: textColor }}
            >{`(${valueLabel})`}</Label>
          </Stripe>
          {subLabel && <SubLabel aria-hidden="true">{subLabel}</SubLabel>}
        </>
      ) : (
        <LeafContent aria-hidden="true">
          <Stripe style={{ height: 'auto', padding: 0, alignItems: 'start' }}>
            {showIcon && icon}
            <LeafLabel style={{ color: textColor }}>
              {leafLines.map((line, index) => (
                <LeafLabelLine key={index}>{line}</LeafLabelLine>
              ))}
            </LeafLabel>
          </Stripe>
          {/* Leaf content rather than a header over a void - there are no children to head -
              but the rule's own label still says what these were grouped by.

              Off the sublabel rather than off `isCutRuleGroup`, which could never fire: only the
              reference, parent and parent-all rules keep a `subLabels`, and all three are built
              by `setParentsAndAddGroupIfNeeded` with a component's `modelId` beside it - so a
              group with a sublabel always has a colour. A cut *reference* group is the case that
              reaches this, and `Uses` is exactly the distinction the sublabel exists for. */}
          {showSubLabel && <SubLabel>{subLabel}</SubLabel>}
        </LeafContent>
      )}
    </NodeElement>
  );
};

export { HierarchicalTreemapViewNode };
